import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SearchHighlightedText } from '../shared/SearchTextareaMirror';
import type { TodoNoteItemDraft } from '../../types/todoNote';
import TodoChecklistText from './TodoChecklistText';
import {
  buildTodoItemFieldSearchMatchId,
  buildTodoMemoBodySearchMatchId,
} from '../../utils/todoNoteSearch';

const VIEW_PAD = 8;
const ANCHOR_GAP = 4;
const CLOSE_DELAY_MS = 140;

export function TodoNoteCalendarItemPopContent({
  item,
  searchQuery,
  activeMatchId,
}: {
  item: Pick<TodoNoteItemDraft, 'id' | 'title' | 'body'>;
  searchQuery: string;
  activeMatchId: string | null;
}) {
  return (
    <div className="cp-todo-note-calendar-item-pop-panel">
      <div className="cp-todo-note-calendar-item-pop-title">
        {searchQuery ? (
          <SearchHighlightedText
            text={item.title || '—'}
            query={searchQuery}
            activeMatchId={activeMatchId}
            buildMatchId={(start) => buildTodoItemFieldSearchMatchId(item.id, 'memo', start)}
          />
        ) : (
          item.title || '—'
        )}
      </div>
      {item.body.trim() ? (
        <div className="cp-todo-note-calendar-item-pop-body">
          {searchQuery ? (
            <SearchHighlightedText
              text={item.body}
              query={searchQuery}
              activeMatchId={activeMatchId}
              buildMatchId={(start) =>
                buildTodoMemoBodySearchMatchId(item.id, start, item.title, item.body)
              }
            />
          ) : (
            <TodoChecklistText text={item.body} />
          )}
        </div>
      ) : null}
    </div>
  );
}

type HoverPopProps = {
  /** 검색 고정 표시(호버 없이 유지) */
  pinned?: boolean;
  /** 셀 안 검색 팝 행 — 인라인 유지 */
  inRow?: boolean;
  children: React.ReactNode;
};

/** 앵커(캘린더 속 할 일 블럭) 위·아래 중 한쪽에만 배치 — 높이는 그쪽 공간까지만(앵커를 덮지 않음) */
function placeInViewport(anchor: DOMRect, popW: number, popH: number): {
  left: number;
  top: number;
  maxHeight: number;
} {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceBelow = vh - VIEW_PAD - (anchor.bottom + ANCHOR_GAP);
  const spaceAbove = anchor.top - ANCHOR_GAP - VIEW_PAD;
  const placeBelow = popH <= spaceBelow || spaceBelow >= spaceAbove;
  const maxHeight = Math.max(40, placeBelow ? spaceBelow : spaceAbove);
  const height = Math.min(popH, maxHeight);
  const top = placeBelow ? anchor.bottom + ANCHOR_GAP : anchor.top - ANCHOR_GAP - height;

  let left = anchor.left;
  if (left + popW > vw - VIEW_PAD) left = vw - VIEW_PAD - popW;
  if (left < VIEW_PAD) left = VIEW_PAD;

  return { left, top, maxHeight };
}

/**
 * 캘린더 할 일 호버/검색 미니 모달.
 * 테이블 overflow에 잘리지 않도록 body 포털 + 뷰포트 클램프.
 */
export function TodoNoteCalendarItemHoverPop({
  pinned = false,
  inRow = false,
  children,
}: HoverPopProps) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number; maxHeight: number } | null>(
    null
  );
  const [clipped, setClipped] = useState(false);

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setHoverOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (inRow || pinned) return;
    const item = hostRef.current?.closest('.cp-todo-note-calendar-item');
    if (!item) return;
    const onEnter = () => {
      clearCloseTimer();
      setHoverOpen(true);
    };
    const onLeave = () => scheduleClose();
    item.addEventListener('mouseenter', onEnter);
    item.addEventListener('mouseleave', onLeave);
    return () => {
      item.removeEventListener('mouseenter', onEnter);
      item.removeEventListener('mouseleave', onLeave);
      clearCloseTimer();
    };
  }, [inRow, pinned]);

  const open = inRow ? false : pinned || hoverOpen;

  useLayoutEffect(() => {
    if (!open || inRow) {
      setCoords(null);
      return;
    }
    const item = hostRef.current?.closest('.cp-todo-note-calendar-item') as HTMLElement | null;
    if (!item) return;

    const update = () => {
      const popEl = popRef.current;
      if (!popEl) return;
      const anchor = item.getBoundingClientRect();
      const popBox = popEl.getBoundingClientRect();
      const panel = popEl.firstElementChild as HTMLElement | null;
      /** max-height로 잘리기 전 원래 높이(테두리 포함) */
      const naturalH = panel ? panel.scrollHeight + (panel.offsetHeight - panel.clientHeight) : popBox.height;
      const next = placeInViewport(anchor, Math.max(popBox.width, 160), Math.max(naturalH, 40));
      setCoords(next);
      setClipped(!!panel && panel.scrollHeight > next.maxHeight - (panel.offsetHeight - panel.clientHeight) + 1);
    };

    update();
    const raf = window.requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, inRow, children]);

  if (inRow) {
    return (
      <div
        className="cp-todo-note-calendar-item-pop cp-todo-note-calendar-item-pop--search-pinned cp-todo-note-calendar-item-pop--in-row"
        role="tooltip"
      >
        {children}
      </div>
    );
  }

  return (
    <>
      <span ref={hostRef} className="cp-todo-note-calendar-item-pop-host" aria-hidden />
      {open
        ? createPortal(
            <div
              ref={popRef}
              className={
                'cp-todo-note-calendar-item-pop cp-todo-note-calendar-item-pop--portal' +
                (pinned ? '' : ' is-hover-clip') +
                (!pinned && clipped ? ' is-clipped' : '')
              }
              role="tooltip"
              style={
                coords
                  ? {
                      left: coords.left,
                      top: coords.top,
                      maxHeight: coords.maxHeight,
                    }
                  : {
                      left: -9999,
                      top: 0,
                      visibility: 'hidden',
                    }
              }
              onMouseEnter={() => {
                clearCloseTimer();
                setHoverOpen(true);
              }}
              onMouseLeave={() => {
                if (!pinned) scheduleClose();
              }}
            >
              {children}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
